import { HIDDEN_FIELDS, PROPERTY_LABELS } from "./inspectorConfig";

export interface InspectorField {
  key: string;
  label: string;
  value: string | string[];
}

const ZERO_VALUE_API_DATE = "0001-01-01T00:00:00Z";
const EMPTY_VALUE_DISPLAY = "—";
const EMPTY_ARRAY_DISPLAY = "NONE";
const AD_NEVER_VALUE = "NEVER";
const AD_UNKNOWN_VALUE = "UNKNOWN";
const SECONDS_LOWER_BOUND = 315536400;
const BITWISE_INTS = new Map([["certificatemappingmethodsraw", 2]]);
const DATE_FIELDS = new Set([
  "lastseen",
  "whencreated",
  "lastlogontimestamp",
  "lastlogon",
  "pwdlastset",
  "lastsuccessfulsignindatetime",
  "lastcollected",
  "firstseen",
]);

const dateTimeFormatter = new Intl.DateTimeFormat(undefined, {
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  timeZoneName: "short",
});

const formatDateTime = (date: Date) => (Number.isNaN(date.getTime()) ? "" : dateTimeFormatter.format(date));

const formatSeconds = (seconds: number) => formatDateTime(new Date(seconds * 1000));

const startCase = (value: string) =>
  value
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim()
    .replace(/\b\w/g, (character) => character.toUpperCase());

export const formatPropertyLabel = (key: string) => PROPERTY_LABELS[key] ?? startCase(key);

const formatAdTime = (value: number, key: string) => {
  switch (key) {
    case "whencreated":
      return value === 0 || value === -1 ? AD_UNKNOWN_VALUE : formatSeconds(value);
    case "lastlogon":
    case "lastlogontimestamp":
      if (value === 0) return AD_UNKNOWN_VALUE;
      if (value === -1) return AD_NEVER_VALUE;
      return formatSeconds(value);
    case "pwdlastset":
      if (value === 0) return "ACCOUNT CREATED BUT NO PASSWORD SET";
      if (value === -1) return AD_NEVER_VALUE;
      return formatSeconds(value);
    default:
      return undefined;
  }
};

const formatNumber = (value: number, key: string) => {
  const adTime = formatAdTime(value, key);
  if (adTime !== undefined) return adTime;
  const bitwisePadding = BITWISE_INTS.get(key);
  if (bitwisePadding) return `${value} (0x${value.toString(16).padStart(bitwisePadding, "0").toUpperCase()})`;
  if (value > SECONDS_LOWER_BOUND && value < Math.round(Date.now() / 1000)) return formatSeconds(value);
  if (value > 0 && value < 1) return `${(value * 100).toFixed(0)}%`;
  return value.toLocaleString();
};

const formatDateString = (value: string) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : formatDateTime(date);
};

export const formatPrimitive = (value: unknown, key: string): string => {
  if (value === null || value === undefined || value === "") return EMPTY_VALUE_DISPLAY;
  if (typeof value === "number") return formatNumber(value, key);
  if (typeof value === "boolean") return value ? "TRUE" : "FALSE";
  if (typeof value === "string") return DATE_FIELDS.has(key) ? formatDateString(value) : value;
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

export const formatFieldValue = (value: unknown, key: string): string | string[] => {
  if (Array.isArray(value)) {
    return value.length ? value.map((item) => formatPrimitive(item, key)) : [EMPTY_ARRAY_DISPLAY];
  }
  return formatPrimitive(value, key);
};

const isDisplayable = (value: unknown) => {
  if (value === undefined || value === ZERO_VALUE_API_DATE) return false;
  if (value !== null && typeof value === "object" && !Array.isArray(value)) {
    return Object.keys(value as object).length > 0;
  }
  return true;
};

export const buildObjectFields = (properties: Record<string, unknown>): InspectorField[] =>
  Object.entries(properties || {})
    .filter(([key, value]) => !HIDDEN_FIELDS.has(key) && isDisplayable(value))
    .map(([key, value]) => ({ key, label: formatPropertyLabel(key), value: formatFieldValue(value, key) }))
    .sort((first, second) => first.label.localeCompare(second.label));

export const fieldValueText = (value: string | string[]) => (Array.isArray(value) ? value.join(", ") : value);
